import type { ApiResult, QuoteMatrix } from './api.js';

export type QuoteMatrixLoadState =
  | { status: 'idle' }
  | { status: 'loading'; demandId: string }
  | { status: 'ready'; demandId: string }
  | { status: 'error'; demandId: string; message: string };

interface QuoteMatrixLoaderDeps {
  request: (orgId: string, demandId: string) => Promise<ApiResult<QuoteMatrix>>;
  currentKey: () => string | undefined;
  setMatrix: (matrix: QuoteMatrix) => void;
  setState: (state: QuoteMatrixLoadState) => void;
  wait?: (milliseconds: number) => Promise<void>;
}

const retryable = (status: number): boolean => status === 408 || status === 429 || status >= 500;

interface InFlight {
  run: Promise<void>;
  /** True until the microtask after the request was sent: calls in that burst share it. */
  sameTick: boolean;
  /** The single follow-up request that later callers share. */
  next?: Promise<void>;
}

export interface QuoteMatrixLoadOptions {
  /**
   * The caller knows the data just changed (a saved quotation, a re-opened demand). It must not join a
   * request already in flight, which may predate the change: it gets one fresh follow-up request once
   * that request settles, shared by any other `fresh` callers (HS2-YRAK4T).
   */
  fresh?: boolean;
}

/**
 * Keep quote loading tied to the demand that started it. Calls for the same org+demand share one
 * request, except `fresh` calls made after the request was sent (see `QuoteMatrixLoadOptions`).
 * Transient failures get one retry, and a late response cannot overwrite the demand opened afterward.
 */
export function createQuoteMatrixLoader(
  deps: QuoteMatrixLoaderDeps,
): (orgId: string, demandId: string, options?: QuoteMatrixLoadOptions) => Promise<void> {
  const inFlight = new Map<string, InFlight>();
  const wait = deps.wait ?? ((milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds)));

  const load = (orgId: string, demandId: string, options?: QuoteMatrixLoadOptions): Promise<void> => {
    const key = `${orgId}:${demandId}`;
    const existing = inFlight.get(key);
    if (existing !== undefined) {
      if (options?.fresh !== true || existing.sameTick) return existing.run;
      // `run` only settles after the entry is removed, so the follow-up starts a fresh request.
      existing.next ??= existing.run.then(() => load(orgId, demandId));
      return existing.next;
    }

    const run = (async (): Promise<void> => {
      deps.setState({ status: 'loading', demandId });
      let result: ApiResult<QuoteMatrix> | undefined;
      let thrown: unknown;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          result = await deps.request(orgId, demandId);
          thrown = undefined;
        } catch (error) {
          result = undefined;
          thrown = error;
        }
        const shouldRetry = result === undefined || (!result.ok && retryable(result.status));
        if (!shouldRetry || attempt === 1) break;
        await wait(150);
      }

      if (deps.currentKey() !== key) return;
      if (result?.ok === true && result.data !== null) {
        deps.setMatrix(result.data);
        deps.setState({ status: 'ready', demandId });
        return;
      }

      const detail = result?.error ?? (thrown instanceof Error ? thrown.message : 'Request failed');
      deps.setState({ status: 'error', demandId, message: `Quotes could not be loaded. ${detail}` });
    })().finally(() => {
      inFlight.delete(key);
    });

    const entry: InFlight = { run, sameTick: true };
    inFlight.set(key, entry);
    queueMicrotask(() => {
      entry.sameTick = false;
    });
    return run;
  };
  return load;
}
